// Batch 8.1 · F-37b / F-37c — Storage Health display helpers (Founder Portal).
//
//  sizeFmt(bytes)            → best unit, 2 decimals: "764.00 KB", "1.23 GB", "0.00 B"
//                              Batch 8.4 · B3 (F-47): DECIMAL units — 1 KB = 1,000 bytes, 1 MB = 1,000,000 bytes — the same
//                              rule as the backend (_mb = bytes / 1e6), the Backups card, the AWS-S3 tab, the Database storage
//                              tab and the CSV export (which carries the raw byte count). Hosting dashboards that count in
//                              1,024s (MiB) show a slightly smaller number for the same bytes — see SIZE_FOOTNOTE.
//  SIZE_FOOTNOTE             → the one sentence shown wherever a size is compared with a hosting dashboard
//  triggerLabel(key, lang)   → readable trigger of a cleanup run / orphan report; never the raw key

// Batch 19 · B1 (F-144): ONE unit everywhere in the Founder portal — GB with 2 decimals, 1 GB = 1,000,000,000 bytes (the portal's
// stated decimal rule). A size under 0.01 GB reads "< 0.01 GB" (never "0.00 GB" — the F-37 problem); exactly nothing reads "0 GB".
// Exports keep the exact byte count.
export const GB = 1_000_000_000;

export function gbFmt(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 GB";
  if (n < GB / 100) return "< 0.01 GB";
  return `${(n / GB).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} GB`;
}

export function gbFromMb(mb) {          // a backend figure already in MB (1 MB = 1,000,000 bytes) → the same GB rule
  const n = Number(mb);
  if (!Number.isFinite(n)) return "—";
  return gbFmt(n * 1_000_000);
}

export function sizeFmt(bytes) {        // the former best-unit helper now follows the GB rule (every caller shows GB)
  return gbFmt(bytes);
}

export const SIZE_FOOTNOTE = "Sizes: GB with 2 decimals, 1 GB = 1,000,000,000 bytes (the same rule everywhere in the Founder portal); a size under 0.01 GB reads \"< 0.01 GB\". A hosting dashboard that counts in 1,024s (GiB) shows a slightly smaller figure for the same bytes. Exports carry the exact byte count.";

const TRIGGERS = {
  scheduled:        { en: "Scheduled",                    ar: "مجدول" },
  scheduled_dryrun: { en: "Scheduled — dry run",          ar: "مجدول — تجربة دون حذف" },
  manual:           { en: "Manual",                       ar: "يدوي" },
  manual_dryrun:    { en: "Manual — dry run",             ar: "يدوي — تجربة دون حذف" },
  cron:             { en: "External trigger",             ar: "تشغيل خارجي" },
  cron_dryrun:      { en: "External trigger — dry run",   ar: "تشغيل خارجي — تجربة دون حذف" },
};

const FALLBACK = { en: "Other", ar: "أخرى" };
const DRY = { en: " — dry run", ar: " — تجربة دون حذف" };

export function triggerLabel(key, lang = "en") {
  const l = lang === "ar" ? "ar" : "en";
  const k = String(key || "").trim().toLowerCase();
  if (TRIGGERS[k]) return TRIGGERS[k][l];
  if (!k) return FALLBACK[l];
  // an unknown key: readable words from the key itself, "_dryrun" / "_dry_run" shown as the dry-run suffix
  const dry = /_?dry_?run$/.test(k);
  const base = k.replace(/_?dry_?run$/, "").replace(/[_-]+/g, " ").trim();
  if (!base) return FALLBACK[l] + (dry ? DRY[l] : "");
  const words = base.charAt(0).toUpperCase() + base.slice(1);
  return (l === "ar" ? `${FALLBACK[l]} (${words})` : words) + (dry ? DRY[l] : "");
}

export default { sizeFmt, gbFmt, gbFromMb, GB, triggerLabel, SIZE_FOOTNOTE };
